import React, { useCallback, useState } from 'react';
import type {
  InvitationDeliveryMode,
  RenderedInvitationMessage,
} from '../../../../../services/invitationSendApi';
import './InvitationIssueResultModal.css';

export interface InvitationIssueResultModalProps {
  deliveryMode: InvitationDeliveryMode;
  sent: number;
  failed: number;
  errors: string[];
  messages: RenderedInvitationMessage[];
  onClose: () => void;
}

async function copyText(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}

const InvitationIssueResultModal: React.FC<InvitationIssueResultModalProps> = ({
  deliveryMode,
  sent,
  failed,
  errors,
  messages,
  onClose,
}) => {
  const [copiedEmails, setCopiedEmails] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2500);
  }, []);

  const handleCopyBody = async (row: RenderedInvitationMessage) => {
    try {
      await copyText(row.body);
      setCopiedEmails((prev) => new Set(prev).add(row.email));
      showToast(`${row.name || row.email} の本文をコピーしました`);
    } catch {
      showToast('コピーに失敗しました');
    }
  };

  const isManual = deliveryMode === 'MANUAL';

  return (
    <div className="invitation-issue-result__overlay" role="dialog" aria-modal="true">
      <div className="invitation-issue-result__box">
        <h2 className="invitation-issue-result__title">
          {isManual ? '招待を発行しました' : '招待を送信しました'}
        </h2>
        <p className="invitation-issue-result__lead">
          {isManual ? (
            <>
              メールは送信していません。各従業員に LINE 等で送るメッセージをコピーしてください。
            </>
          ) : (
            <>
              {sent > 0 && `${sent}件送信しました。`}
              {failed > 0 && ` ${failed}件失敗しました。`}
              {sent > 0 && ' 必要に応じて本文をコピーして他の手段で送れます。'}
            </>
          )}
        </p>
        {errors.length > 0 && (
          <ul className="invitation-issue-result__errors">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}
        {messages.length > 0 && (
          <>
            <p className="invitation-issue-result__warning">
              本文には初期パスワードが含まれます。本人への直接送信に限定してください。
            </p>
            <div className="invitation-issue-result__table-wrap">
              <table className="invitation-issue-result__table">
                <thead>
                  <tr>
                    <th>氏名</th>
                    <th>メール</th>
                    <th aria-label="操作">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((row) => (
                    <tr key={row.email}>
                      <td>{row.name || '—'}</td>
                      <td className="invitation-issue-result__email">{row.email}</td>
                      <td>
                        <button
                          type="button"
                          className="invitation-issue-result__copy-btn"
                          onClick={() => void handleCopyBody(row)}
                        >
                          {copiedEmails.has(row.email) ? '再コピー' : '本文をコピー'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="invitation-issue-result__footer">
          <button type="button" className="invitation-issue-result__close-btn" onClick={onClose}>
            招待一覧へ
          </button>
        </div>
        {toast && <div className="invitation-issue-result__toast">{toast}</div>}
      </div>
    </div>
  );
};

export default InvitationIssueResultModal;
